import { jwtDecode } from 'jwt-decode'
import {
  AppRouterInstance,
  NavigateOptions,
} from 'next/dist/shared/lib/app-router-context.shared-runtime'
import { ReadonlyURLSearchParams } from './navigationUtils'

export function isIos() {
  if (typeof navigator === 'undefined') return false
  const userAgent = navigator.userAgent

  // iOS Detection
  if (/iPad|iPhone|iPod/.test(userAgent)) {
    return true
  }
  return false
}
export function getOSVersion() {
  if (typeof navigator === 'undefined') return false
  const userAgent = navigator.userAgent

  // iOS Detection
  if (/iPad|iPhone|iPod/.test(userAgent)) {
    const match = userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/)
    if (match) {
      return 'iOS ' + match[1] + '.' + match[2] + (match[3] ? '.' + match[3] : '')
    }
  }

  // Android Detection
  if (/android/i.test(userAgent)) {
    const match = userAgent.match(/Android\s([0-9\.]+)/)
    if (match) {
      return 'Android ' + match[1]
    }
  }

  return 'Unknown OS'
}

export const checkLocalStorage = () => {
  if (typeof window !== 'undefined' && typeof window?.localStorage !== 'undefined') {
    return true
  } else {
    return false
  }
}

export const handleSearchParam = (
  searchParams: ReadonlyURLSearchParams,
  pathname: string,
  router: AppRouterInstance,
  obj: { [x: string]: string },
  navigateOptions?: NavigateOptions,
) => {
  const current = searchParams && new URLSearchParams(Array.from(searchParams.entries()))
  for (const [key, val] of Object.entries(obj)) {
    current.set(key, val)
  }
  const search = current.toString()
  const query = search ? `?${search}` : ''
  router.replace(`${pathname}${query}`, navigateOptions)
  return `${pathname}${query}`
}

export const removeSearchParam = (
  searchParams: ReadonlyURLSearchParams,
  pathname: string,
  router: AppRouterInstance,
  arr: string[],
  navigateOptions?: NavigateOptions,
) => {
  const current = new URLSearchParams(Array.from(searchParams.entries()))
  arr.forEach((key) => {
    current.delete(key)
  })
  const search = current.toString()
  const query = search ? `?${search}` : ''
  void router.replace(`${pathname}${query}`, navigateOptions)
}

export const convertURLParamsToObject = (
  params: ReadonlyURLSearchParams,
): { [key: string]: string } => {
  const URLparams = new URLSearchParams(Array.from(params.entries()))
  return Object.fromEntries(URLparams)
}

const hyphenFormatter = (params: { value: any }) => {
  if (params.value == null || params.value === '') {
    return '-'
  }
  return params.value
}

export const addHyphenForBlankFieldsInAgGridTable = (
  col_defs: { field?: string; headerName: string }[],
) => {
  const formatted = col_defs.map((col_def) => {
    return { ...col_def, valueFormatter: hyphenFormatter }
  })
  return formatted
}

export const addAndRemoveSearchParam = (
  searchParams: ReadonlyURLSearchParams,
  pathname: string,
  router: AppRouterInstance,
  newParams: { [x: string]: string },
  removeParams: string[],
  navigateOptions?: NavigateOptions,
) => {
  const current = new URLSearchParams(Array.from(searchParams.entries()))
  removeParams.forEach((key) => {
    current.delete(key)
  })
  for (const [key, val] of Object.entries(newParams)) {
    current.set(key, val)
  }
  const search = current.toString()
  const query = search ? `?${search}` : ''
  router.replace(`${pathname}${query}`, navigateOptions)
}

export const checkIfArray = (arr?: Array<any>) => {
  return !!(arr && Array.isArray(arr) && arr.length && arr[0])
}

export const checkIfObject = (obj: { [x: string]: any }) => {
  return !!(obj && Object.keys(obj).length !== 0)
}

export const getKeyByValue = (object: { [x: string]: string }, value: string) => {
  return Object.keys(object).find((key) => object[key] === value)
}

export const getValue = (val: any) => {
  return val?.value || val
}

export const getLabel = (val: any) => {
  return val?.label || val
}

type Nav = {
  sub_tabs?: SubTab[]
}

type SubTab = {
  allowedPlatforms?: string[]
}

type NavigationData = {
  [key: string]: Nav[]
}

export const isMobile = () => {
  if (typeof navigator === 'undefined') return false
  if (typeof window !== 'undefined') {
    return window.innerWidth < 768
  }
  return false
}

export function isAllenTeacherApp() {
  return navigator?.userAgent === 'allenTeacherApp' || false
}

export const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false
  const details = navigator.userAgent
  const regexp = /android|iphone|kindle|ipad/i
  return regexp.test(details)
}

export const getNavigationForDevice = (navigationData: NavigationData) => {
  const navData: NavigationData = {}
  for (const key in navigationData) {
    const navs = navigationData[key] || []
    const data = navs.filter((nav: Nav) => {
      if (nav?.sub_tabs?.length ?? 0 > 0) {
        const subTab = nav?.sub_tabs?.filter((subTab: SubTab) => {
          return subTab?.allowedPlatforms?.includes(isMobile() ? 'mweb' : 'web')
        })
        return subTab?.length ?? 0 > 0
      } else {
        return nav
      }
    })
    if (data.length > 0) {
      navData[key] = data
    }
  }
  return navData
}

export const tokenExpired = (token: string) => {
  if (!token) {
    return true
  }
  const decodedToken: any = jwtDecode(token)
  const currentDate = new Date()
  if (decodedToken.exp * 1000 < currentDate.getTime()) {
    return true
  } else {
    return false
  }
}
export function getToken(): string {
  try {
    if (typeof window != 'undefined') {
      const tokens = localStorage.getItem('tokens')
      return tokens ? JSON.parse(tokens)['x-access-token'] : ''
    }
    return ''
  } catch (error) {
    console.log(error)
    return ''
  }
}

export function getUserIdFromToken(token: string) {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        })
        .join(''),
    )

    const parseToken = JSON.parse(jsonPayload)
    return parseToken?.uid || ''
  } catch (error) {
    console.log(error)
    return ''
  }
}

export function getUserId() {
  const token = getToken()
  const userId = getUserIdFromToken(token)
  return userId
}

export const getExtension = (name: string) => {
  return `.` + name.slice(((name.lastIndexOf('.') - 1) >>> 0) + 2)
}

export const getDateRangeFromAcademicSessionKeys = (session: string): { [key: string]: Date } => {
  let [startRange, endRange] = session.split('__')

  startRange = `${startRange.split('_').join('/01/')}`
  endRange = `${endRange.split('_').join('/01/')}`

  const startDate = new Date(startRange)
  startDate.setMonth(startDate.getMonth() - 30)
  const endDate = new Date(endRange)
  endDate.setMonth(endDate.getMonth() + 30)

  return { startRange: startDate, endRange: endDate }
}

export const reloadWindow = (delay = 2000) => {
  setTimeout(() => {
    try {
      if (window !== undefined && window?.location !== undefined) location.reload()
    } catch (error) {
      console.log(error)
    }
  }, delay)
}

export function buildQueryString(
  params: Record<string, string | number | boolean | Array<string | number | boolean>>,
) {
  const encodedParams = Object.entries(params)
    .map(([key, value]) => {
      let encodedValue = value
      if (Array.isArray(value)) {
        encodedValue = value.map((v) => encodeURIComponent(v)).join(',')
      } else {
        encodedValue = encodeURIComponent(value)
      }
      return `${encodeURIComponent(key)}=${encodedValue}`
    })
    .join('&')
  return encodedParams
}

export const getErrorsMessage = (errorList: { [x: string]: string }) => {
  const returnVal: string[] = []
  errorList &&
    checkIfObject(errorList) &&
    Object.keys(errorList).forEach((ele) => returnVal.push(...Object.values(errorList[ele])))
  return returnVal
}

export const removeExtension = (name: string, fileExtension: string[]) => {
  let returnVal = name
  fileExtension.forEach((ele) => {
    returnVal = returnVal.replace(ele, '')
  })
  return returnVal
}
export const getNestedValue = <T>(obj: T, path: string): T | undefined => {
  const keys = path.split('.')
  let result = obj

  for (const key of keys) {
    if (
      result === undefined ||
      result === null ||
      typeof result === 'string' ||
      typeof result === 'number' ||
      !(typeof result === 'object' && key in result)
    ) {
      return undefined
    } else {
      result = (result as T[] | any)[key]
    }
  }
  return result
}

function updateAndCopy<T, K = unknown>(data: T, path: string[], newValue: K): T {
  if (!path.length) {
    return newValue as unknown as T
  }

  const key = path[0]

  if (!data || typeof data === 'string' || typeof data === 'number') {
    throw new Error('Can not access ' + key + ' of ' + typeof data)
  }

  if (Array.isArray(data)) {
    const itemIndex = parseInt(key)

    return [
      ...data.slice(0, itemIndex),
      updateAndCopy(data[itemIndex], path.slice(1), newValue),
      ...data.slice(itemIndex + 1),
    ] as T
  }

  const obj = (data as any)[key]

  return { ...data, [key]: updateAndCopy<typeof obj>(obj, path.slice(1), newValue) }
}

/**
 * It will create a copy of provided object only where update is needed
 * @param data data object to be updated
 * @param path nested path of the
 * @param newValue new value at nested path in the object
 * @returns referenced copied object
 */
export function updatedNestedValue<T, K>(data: T, path: string, newValue: K) {
  return updateAndCopy(data, path.split('.'), newValue)
}
export const isRegularOrSpecialBatchSyllabusType = (syllabusType: string): boolean => {
  return syllabusType === 'regular_batch' || syllabusType === 'special_batch'
}

export function capitalizeFirstLetter(text: string) {
  if (!text || !(typeof text === 'string')) return text
  return text.charAt(0).toUpperCase() + text.slice(1)
}
